import type { TFunction } from "i18next";

import {
  ConflictErrorSeverity,
  GenericUploadRowConflictError,
  type IDynamicObject,
  type IPassengerPhoneInUseItemDto,
  type IPassengerPhoneInUseOwnerDto,
} from "@ryde/shared";

import {
  GENERIC_UPLOAD_PHONE_FIELD,
  UPLOAD_PHONE_ISRAEL_COUNTRY_CODE,
  UPLOAD_PHONE_NATIONAL_LENGTH_WITHOUT_TRUNK,
  UPLOAD_PHONE_NATIONAL_TRUNK_PREFIX,
} from "../generic-upload.constants";
import type {
  IGenericUploadPhoneConflict,
  IGenericUploadPhoneConflictRowLabel,
} from "../generic-upload.models";

const NON_DIGITS_PATTERN = /\D/g;
const LABEL_SEPARATOR = " · ";
const EMPTY_LABEL = "-";

export const readUploadRowPhone = (row: IDynamicObject): string =>
  String(row[GENERIC_UPLOAD_PHONE_FIELD] ?? "").trim();

export const collectUploadPhones = (data: IDynamicObject[]): string[] =>
  data.map(readUploadRowPhone);

export const readUploadRowId = (row: IDynamicObject): number | undefined => {
  const rowId: unknown = row.id;

  if (rowId === undefined || rowId === null || rowId === "") {
    return undefined;
  }

  const numericId = Number(rowId);

  return Number.isNaN(numericId) ? undefined : numericId;
};

/**
 * Stand-in for the backend normalizer, used only when the phones-in-use check did not run. It folds the shapes an
 * Israeli sheet holds onto the stored form — "+972 52…", "972…", "052-…" and a trunk-less "52…" — so one number
 * typed several ways inside the same file compares equal. It never decides whether a number is valid.
 */
export const fallbackNormalizeUploadPhone = (phone: string): string | null => {
  const digits = phone.replace(NON_DIGITS_PATTERN, "");

  if (!digits) {
    return null;
  }

  if (digits.startsWith(UPLOAD_PHONE_ISRAEL_COUNTRY_CODE)) {
    return digits;
  }

  if (digits.startsWith(UPLOAD_PHONE_NATIONAL_TRUNK_PREFIX)) {
    return `${UPLOAD_PHONE_ISRAEL_COUNTRY_CODE}${digits.slice(UPLOAD_PHONE_NATIONAL_TRUNK_PREFIX.length)}`;
  }

  return digits.length === UPLOAD_PHONE_NATIONAL_LENGTH_WITHOUT_TRUNK
    ? `${UPLOAD_PHONE_ISRAEL_COUNTRY_CODE}${digits}`
    : digits;
};

/**
 * The backend answers one item per phone, in request order, echoing the phone it was given. A response of another
 * length, or whose echoes do not match, cannot be attributed to rows and is refused as a whole; the caller then
 * treats the existing-passenger check as not run, which still leaves the in-file duplicate check on the fallback.
 */
export const alignPhonesInUseItems = ({
  phones,
  items,
}: {
  phones: string[];
  items: IPassengerPhoneInUseItemDto[] | null;
}): IPassengerPhoneInUseItemDto[] | null => {
  if (items?.length !== phones.length) {
    return null;
  }

  return items.every((item, position) => item.phone === phones[position])
    ? items
    : null;
};

export const buildPhoneConflictRowLabel = (
  row: IDynamicObject,
): IGenericUploadPhoneConflictRowLabel => {
  const rowLabel: IGenericUploadPhoneConflictRowLabel = {
    code: String(row.code ?? ""),
    fullName: [row.firstName, row.lastName]
      .filter(Boolean)
      .map(String)
      .join(" "),
  };

  return rowLabel;
};

/**
 * One conflict per (row, reason). A row can carry both reasons: its phone repeats inside the file AND already
 * belongs to a live passenger. Rows whose phone is empty or unparseable (`normalizedPhone` null) never conflict.
 * An update row that is itself among the phone's holders is exempt: the phone is then unchanged for it and the
 * backend guard (which checks a CHANGED phone only) accepts it, whatever other row also holds the number.
 * `items` must be aligned with `data` by position (`alignPhonesInUseItems`); anything else is treated as "the
 * existing-passenger check did not run", which still leaves the in-file duplicate check on the fallback normalizer.
 */
export const buildPhoneConflicts = ({
  data,
  items,
}: {
  data: IDynamicObject[];
  items: IPassengerPhoneInUseItemDto[] | null;
}): IGenericUploadPhoneConflict[] => {
  const phones = collectUploadPhones(data);
  const alignedItems = alignPhonesInUseItems({ phones, items });
  const normalizedPhones = phones.map((phone, position) => {
    if (!phone) {
      return null;
    }

    return alignedItems
      ? alignedItems[position].normalizedPhone
      : fallbackNormalizeUploadPhone(phone);
  });

  const positionsByPhone = new Map<string, number[]>();

  normalizedPhones.forEach((normalizedPhone, position) => {
    if (!normalizedPhone) {
      return;
    }

    positionsByPhone.set(normalizedPhone, [
      ...(positionsByPhone.get(normalizedPhone) ?? []),
      position,
    ]);
  });

  return data.flatMap((row, dataIndex) => {
    const normalizedPhone = normalizedPhones[dataIndex];

    if (!normalizedPhone) {
      return [];
    }

    const base: Omit<IGenericUploadPhoneConflict, "errorType"> = {
      index: dataIndex + 1,
      dataIndex,
      errorSeverity: ConflictErrorSeverity.Critical,
      field: GENERIC_UPLOAD_PHONE_FIELD,
      fieldValue: phones[dataIndex],
      rowLabel: buildPhoneConflictRowLabel(row),
    };
    const conflicts: IGenericUploadPhoneConflict[] = [];
    const duplicateDataIndexes = (
      positionsByPhone.get(normalizedPhone) ?? []
    ).filter(position => position !== dataIndex);

    if (duplicateDataIndexes.length) {
      conflicts.push({
        ...base,
        errorType: GenericUploadRowConflictError.PhoneDuplicatedInFile,
        duplicateDataIndexes,
      });
    }

    const holders = alignedItems?.[dataIndex].passengers ?? [];
    const isPhoneTaken =
      holders.length > 0 ||
      Boolean(alignedItems?.[dataIndex].hasHolderOutsideScope);
    const rowId = readUploadRowId(row);
    const isRowAmongHolders =
      rowId !== undefined && holders.some(holder => holder.id === rowId);

    // A holder outside the caller's scope is not listed, so the conflict is shown without a name.
    if (isPhoneTaken && !isRowAmongHolders) {
      conflicts.push({
        ...base,
        errorType: GenericUploadRowConflictError.PhoneAlreadyInUse,
        existingPassenger: holders[0],
      });
    }

    return conflicts;
  });
};

export const toggleSkippedDataIndex = (
  skippedDataIndexes: number[],
  dataIndex: number,
): number[] =>
  skippedDataIndexes.includes(dataIndex)
    ? skippedDataIndexes.filter(index => index !== dataIndex)
    : [...skippedDataIndexes, dataIndex];

export const excludeSkippedUploadRows = <T>(
  data: T[],
  skippedDataIndexes: number[],
): T[] => {
  if (!skippedDataIndexes.length) {
    return data;
  }

  const skipped = new Set(skippedDataIndexes);

  return data.filter((_, dataIndex) => !skipped.has(dataIndex));
};

/**
 * A skipped row resolves its own conflicts. An in-file duplicate is also resolved once every other row sharing the
 * phone is skipped — keeping one of the twins yields a valid payload. An existing-passenger conflict only goes away
 * with the row itself.
 */
export const isPhoneConflictUnresolved = (
  conflict: IGenericUploadPhoneConflict,
  skippedDataIndexes: Set<number>,
): boolean => {
  if (skippedDataIndexes.has(conflict.dataIndex)) {
    return false;
  }

  if (
    conflict.errorType !== GenericUploadRowConflictError.PhoneDuplicatedInFile
  ) {
    return true;
  }

  return (conflict.duplicateDataIndexes ?? []).some(
    position => !skippedDataIndexes.has(position),
  );
};

/** Rows, not conflicts: a row carrying both reasons is one decision for the user. */
export const countUnresolvedPhoneConflictRows = (
  conflicts: IGenericUploadPhoneConflict[],
  skippedDataIndexes: number[],
): number => {
  const skipped = new Set(skippedDataIndexes);
  const unresolvedRows = new Set(
    conflicts
      .filter(conflict => isPhoneConflictUnresolved(conflict, skipped))
      .map(conflict => conflict.dataIndex),
  );

  return unresolvedRows.size;
};

export const formatDuplicateRowsLabel = (
  duplicateDataIndexes: number[],
): string =>
  duplicateDataIndexes.map(position => String(position + 1)).join(", ");

export const formatPhoneConflictPassengerLabel = ({
  code,
  fullName,
}: IGenericUploadPhoneConflictRowLabel): string =>
  [code, fullName].filter(Boolean).join(LABEL_SEPARATOR) || EMPTY_LABEL;

export const formatPhoneConflictOwnerLabel = (
  owner: IPassengerPhoneInUseOwnerDto,
): string => {
  const fullName = [owner.firstName, owner.lastName].filter(Boolean).join(" ");

  return (
    [owner.code, fullName].filter(Boolean).join(LABEL_SEPARATOR) ||
    String(owner.id)
  );
};

export const formatPhoneConflictDetails = (
  conflict: IGenericUploadPhoneConflict,
  t: TFunction,
): string => {
  if (conflict.errorType === GenericUploadRowConflictError.PhoneAlreadyInUse) {
    return conflict.existingPassenger
      ? t("genericUpload.phoneConflicts.existingPassenger", {
          passenger: formatPhoneConflictOwnerLabel(conflict.existingPassenger),
        })
      : t("genericUpload.conflicts.PhoneAlreadyInUse");
  }

  return t("genericUpload.phoneConflicts.duplicateRows", {
    rows: formatDuplicateRowsLabel(conflict.duplicateDataIndexes ?? []),
  });
};
